import React from 'react';
import { get } from 'lodash/fp';
import { priceFormatter } from 'utils/formatters';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FONTS } from 'styles/theme/fonts';
import { COLORS } from 'styles/theme/colors';

export const ProductDisplay = ({ product, isDetailedView }) => {
  const productImage =
    get('productTemplate.gridPictureUrl')(product) || get('gridPictureUrl')(product);
  const productName = get('productTemplate.name')(product) || get('name')(product);
  const productSku = get('productTemplate.sku')(product) || get('name')(product);
  const productCurrentPrice = priceFormatter(get('priceCents')(product));
  const productLastSoldPrice = priceFormatter(get('lastSoldPriceCents')(product));
  const productLowestPrice = priceFormatter(get('lowestPriceCents')(product));
  const productSize = get('sizeOption.presentation')(product);
  const productSizeUnit = get('productTemplate.sizeUnit')(product) || get('sizeUnit')(product);
  const productGender =
    get('productTemplate.singleGender')(product) || get('singleGender')(product);

  return (
    <React.Fragment>
      <ProductDisplay.ImageAndNameContainer>
        <ProductDisplay.ProductImage src={productImage} />
        <ProductDisplay.ProductName>{productName}</ProductDisplay.ProductName>
        {isDetailedView && (
          <>
            <ProductDisplay.ProductSku>{productSku}</ProductDisplay.ProductSku>
            <ProductDisplay.ProductSizeAndGender>{`${productSize} ${productSizeUnit} ${productGender}`}</ProductDisplay.ProductSizeAndGender>
          </>
        )}
      </ProductDisplay.ImageAndNameContainer>
      <ProductDisplay.Cell isDetailedView={isDetailedView}>
        {!isDetailedView && (
          <>
            <ProductDisplay.PriceInnerContainer>
              <ProductDisplay.PriceHeader>Current</ProductDisplay.PriceHeader>
              <ProductDisplay.Price>${productCurrentPrice}</ProductDisplay.Price>
            </ProductDisplay.PriceInnerContainer>
            <ProductDisplay.Divider />
            <ProductDisplay.PriceInnerContainer>
              <ProductDisplay.PriceHeader>Last Sold</ProductDisplay.PriceHeader>
              <ProductDisplay.Price>${productLastSoldPrice}</ProductDisplay.Price>
            </ProductDisplay.PriceInnerContainer>
            <ProductDisplay.PriceInnerContainer>
              <ProductDisplay.PriceHeader>Lowest</ProductDisplay.PriceHeader>
              <ProductDisplay.Price>${productLowestPrice}</ProductDisplay.Price>
            </ProductDisplay.PriceInnerContainer>
          </>
        )}
        {isDetailedView && (
          <ProductDisplay.PriceInnerContainer isDetailedView>
            <ProductDisplay.PriceHeader>Sold Price</ProductDisplay.PriceHeader>
            <ProductDisplay.Price>${productCurrentPrice}</ProductDisplay.Price>
          </ProductDisplay.PriceInnerContainer>
        )}
      </ProductDisplay.Cell>
    </React.Fragment>
  );
};

ProductDisplay.ImageAndNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${COLORS.primaryProductDisplayBorder};
  width: 365px;
  align-items: center;
  padding: 5px 70px 37px 70px;
`;

ProductDisplay.ProductImage = styled.img`
  width: 225px;
`;

ProductDisplay.ProductName = styled.span`
  font-family: ${FONTS.default};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: ${COLORS.primaryProductDisplayText};
  text-tranform: capitalize;
`;

ProductDisplay.ProductSku = styled.span`
  font-family: ${FONTS.special};
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 1px;
  color: ${COLORS.productDisplayAdditionalText};
  text-transform: uppercase;
  margin: 8px;
`;

ProductDisplay.ProductSizeAndGender = styled.span`
  font-family: ${FONTS.emphasized};
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: 3px;
  color: ${COLORS.secondaryProductDisplayText};
  text-transform: uppercase;
  margin-bottom: 29px;
`;

ProductDisplay.Cell = styled.div`
  border: 1px solid ${COLORS.primaryProductDisplayBorder};
  border-top: none;
  width: 365px;
  height: 85px;
  padding: 23px 23px;
  display: flex;
  justify-content: ${(props) => (props.isDetailedView ? 'center' : 'space-between')};
  position: relative;
`;

ProductDisplay.PriceInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: ${(props) => (props.isDetailedView ? 'center' : 'left')};
`;

ProductDisplay.PriceHeader = styled.span`
  font-family: ${FONTS.default};
  font-size: 12px;
  line-height: 16px;
  text-transform: capitalize;
  color: ${COLORS.productDisplayAdditionalText};
  letter-spacing: 0.5px;
`;

ProductDisplay.Price = styled.span`
  font-family: ${FONTS.default};
  font-size: 14px;
  line-height: 20px;
  color: #d7d7d7;
  letter-spacing: 0.25px;
`;

ProductDisplay.Divider = styled.div`
  background-color: ${COLORS.primaryProductDisplayBackground};
  width: 2px;
  height: 85px;
  margin-top: -23px;
`;

ProductDisplay.propTypes = {
  product: PropTypes.shape({
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  isDetailedView: PropTypes.bool,
};

export default ProductDisplay;
